import type {
  ActiveAccount,
  AvailableAccount,
} from "@/hooks/useActiveAccounts";
import type {
  FilteredBeneficiaryDetail,
  FilteredResolvedBlockchainBeneficiary,
} from "@generated/types";

interface CurrencyAvailabilityParams {
  activeAccounts?: Record<string, ActiveAccount>;
  availableAccounts?: Record<string, AvailableAccount>;
  /** When provided, same-currency payments bypass canTrade check since no exchange is needed */
  beneficiaryCurrency?: string;
  /**
   * The customer's known-currency universe (tradable ∪ payable ids). When
   * provided, a code absent from every account map is considered available
   * ONLY if it appears here — this rejects garbage codes (e.g. a hand-typed
   * `?sell=XYZ` URL param) instead of echoing them, while keeping
   * tradable-but-unheld currencies selectable. When omitted, the permissive
   * legacy default (`true`) is preserved for callers without capability data.
   */
  knownCurrencyCodes?: ReadonlySet<string>;
}

interface FindAlternativeCurrencyParams extends CurrencyAvailabilityParams {
  sortedActiveAccountKeys: string[];
  baseCurrency?: string;
  /** When provided, same-currency payments bypass canTrade check since no exchange is needed */
  beneficiaryCurrency?: string;
}

interface DetermineCurrencyParams extends CurrencyAvailabilityParams {
  sortedActiveAccountKeys: string[];
  baseCurrency?: string;
  customerAccountBaseCurrency?: string;
  beneficiary?:
    | FilteredBeneficiaryDetail
    | FilteredResolvedBlockchainBeneficiary;
}

/**
 * Helper function to check if a currency is available for trading.
 * For same-currency payments (currency matches beneficiaryCurrency),
 * we bypass the canTrade check since no exchange is needed.
 */
export const isCurrencyAvailable = (
  currency: string,
  {
    activeAccounts,
    availableAccounts,
    beneficiaryCurrency,
    knownCurrencyCodes,
  }: CurrencyAvailabilityParams
): boolean => {
  // Same-currency payments don't require exchange, so skip canTrade check
  if (beneficiaryCurrency && currency === beneficiaryCurrency) {
    return true;
  }

  // If the currency is a held or settlement-method account, use its real
  // canTrade flag (active account wins over available, matching the previous
  // `?? ` precedence).
  const accountCanTrade =
    activeAccounts?.[currency]?.canTrade ??
    availableAccounts?.[currency]?.canTrade;
  if (accountCanTrade !== undefined) {
    return accountCanTrade;
  }

  // Absent from every account map. If we know the customer's currency universe,
  // the code is available only if it's a real currency in it — this rejects
  // garbage (e.g. `?sell=XYZ`) while keeping tradable-but-unheld currencies
  // selectable. Without that universe, preserve the permissive legacy default.
  if (knownCurrencyCodes) {
    return knownCurrencyCodes.has(currency);
  }
  return true;
};

/**
 * Helper function to find alternative currency
 */
export const findAlternativeCurrency = (
  excludeCurrency: string,
  {
    sortedActiveAccountKeys,
    baseCurrency,
    activeAccounts,
    availableAccounts,
    beneficiaryCurrency,
    knownCurrencyCodes,
  }: FindAlternativeCurrencyParams
): string => {
  const availabilityCtx: CurrencyAvailabilityParams = {
    activeAccounts,
    availableAccounts,
    beneficiaryCurrency,
    knownCurrencyCodes,
  };

  // Helper to find first available currency from a list
  const findFirstAvailable = (currencies: string[]) =>
    currencies.find(
      (curr) =>
        curr !== excludeCurrency && isCurrencyAvailable(curr, availabilityCtx)
    );

  // Try to find an alternative currency that's not the excluded one and is available
  const alternative = findFirstAvailable(sortedActiveAccountKeys);

  if (alternative) {
    return alternative;
  }

  // If no alternative found and base currency is different and available, use that
  if (
    baseCurrency &&
    baseCurrency !== excludeCurrency &&
    isCurrencyAvailable(baseCurrency, availabilityCtx)
  ) {
    return baseCurrency;
  }

  // Last resort: return empty string to indicate no alternative found
  return "";
};

/**
 * Helper to extract beneficiary currency from beneficiary object
 */
export const getBeneficiaryCurrency = (
  beneficiary?:
    | FilteredBeneficiaryDetail
    | FilteredResolvedBlockchainBeneficiary
): string | undefined => {
  if (!beneficiary) return undefined;
  if (beneficiary.resourceType === "beneficiaries") {
    return (beneficiary as FilteredBeneficiaryDetail).currency;
  }
  if (beneficiary.resourceType === "blockchain-beneficiaries") {
    return (beneficiary as FilteredResolvedBlockchainBeneficiary).asset
      ?.ledgerProduct;
  }
  return undefined;
};

/**
 * Main helper function to determine currency with proper priority.
 * For payment flows, same-currency payments (matching beneficiary currency)
 * bypass the canTrade check since no exchange is needed.
 */
export const determineCurrency = (
  preferredCurrency: string | undefined,
  excludeCurrency: string | undefined,
  useBeneficiaryCurrency: boolean | undefined,
  {
    sortedActiveAccountKeys,
    baseCurrency,
    customerAccountBaseCurrency,
    beneficiary,
    activeAccounts,
    availableAccounts,
    knownCurrencyCodes,
  }: DetermineCurrencyParams
): string => {
  // Extract beneficiary currency for same-currency payment bypass
  const beneficiaryCurrency = getBeneficiaryCurrency(beneficiary);

  if (useBeneficiaryCurrency && beneficiaryCurrency) {
    return beneficiaryCurrency;
  }

  const availabilityCtx: CurrencyAvailabilityParams = {
    activeAccounts,
    availableAccounts,
    beneficiaryCurrency,
    knownCurrencyCodes,
  };

  // Helper to find first available currency from a list
  const findFirstAvailable = (currencies: string[]) =>
    currencies.find(
      (curr) =>
        (!excludeCurrency || curr !== excludeCurrency) &&
        isCurrencyAvailable(curr, availabilityCtx)
    );

  // Priority order:
  // 1. Preferred currency (if available and not excluded)
  if (
    preferredCurrency &&
    (!excludeCurrency || preferredCurrency !== excludeCurrency) &&
    isCurrencyAvailable(preferredCurrency, availabilityCtx)
  ) {
    return preferredCurrency;
  }

  // 3. First available active account
  const activeOption = findFirstAvailable(sortedActiveAccountKeys);
  if (activeOption) return activeOption;

  // 4. First available account from available (settlement-method) accounts
  const availableOption = findFirstAvailable(
    Object.keys(availableAccounts ?? {})
  );
  if (availableOption) return availableOption;

  // 5. Base currency (if available and not excluded)
  if (
    baseCurrency &&
    (!excludeCurrency || baseCurrency !== excludeCurrency) &&
    isCurrencyAvailable(baseCurrency, availabilityCtx)
  ) {
    return baseCurrency;
  }

  // 2. Customer account base currency (if available and not excluded)
  if (
    customerAccountBaseCurrency &&
    (!excludeCurrency || customerAccountBaseCurrency !== excludeCurrency) &&
    isCurrencyAvailable(customerAccountBaseCurrency, availabilityCtx)
  ) {
    return customerAccountBaseCurrency;
  }

  // 6. Empty string as last resort
  return "";
};
